/**
 * 固定 uid/gid（ARCHITECTURE.md §4.9 が唯一の正）。
 *
 * **コンテナ跨ぎで数値が一致していないと Maildir が読めなくなる。**
 * Dockerfile の `useradd -u`、Dovecot の `mail_uid`/`mail_gid`、
 * `chown` を行うエージェントのコードは必ずこの定数を参照すること。
 *
 * 設計書 04 の「vmail=5010 / agent=5000」は廃止。
 */

export interface SystemAccount {
  readonly name: string;
  readonly uid: number;
  readonly gid: number;
  /** 補助グループ（数値 gid）。コンテナ間でファイルを渡すために必要。 */
  readonly supplementaryGids: readonly number[];
  readonly purpose: string;
}

export const UID = {
  vmail: 5000,
  mailsv: 5001,
  mnpostfix: 5002,
  mndovecot: 5003,
  mnrspamd: 5004,
} as const;

export const GID = {
  vmail: 5000,
  mailsv: 5001,
  mnpostfix: 5002,
  mndovecot: 5003,
  mnrspamd: 5004,
} as const;

export const SYSTEM_ACCOUNTS: Readonly<Record<keyof typeof UID, SystemAccount>> = {
  vmail: {
    name: 'vmail',
    uid: UID.vmail,
    gid: GID.vmail,
    supplementaryGids: [],
    purpose: 'Maildir 所有者。Dovecot の mail_uid/mail_gid/first_valid_uid/last_valid_uid',
  },
  mailsv: {
    name: 'mailsv',
    uid: UID.mailsv,
    gid: GID.mailsv,
    supplementaryGids: [],
    purpose: 'エージェントプロセス。設定ツリーの所有者',
  },
  mnpostfix: {
    name: 'mnpostfix',
    uid: UID.mnpostfix,
    gid: GID.mnpostfix,
    // 設定ツリー（mailsv 所有）を読む必要がある。
    supplementaryGids: [GID.mailsv],
    purpose: 'postfix コンテナ内の postfix ユーザ',
  },
  mndovecot: {
    name: 'mndovecot',
    uid: UID.mndovecot,
    gid: GID.mndovecot,
    // 設定ツリーに加えて Maildir と TLS 秘密鍵（0640 root:5000）を読む。
    supplementaryGids: [GID.mailsv, GID.vmail],
    purpose: 'dovecot',
  },
  mnrspamd: {
    name: 'mnrspamd',
    uid: UID.mnrspamd,
    gid: GID.mnrspamd,
    supplementaryGids: [GID.mailsv],
    purpose: 'rspamd',
  },
} as const;

/** Dovecot の `first_valid_uid` / `last_valid_uid`。vmail 以外での配送を構造的に禁じる。 */
export const DOVECOT_VALID_UID_RANGE = { first: UID.vmail, last: UID.vmail } as const;
